import { queryOptions } from '@tanstack/react-query';

import { queryClient } from '@/services/query-client';

import { fetchCarriers } from '../data-provider';
import type { Carrier, CarrierFilters, Carriers } from '../models';
import { CACHE_KEYS } from '../types';
import { logger } from '../utils/logger';

/**
 * Internal carrier lookup cache used to resolve carrier references
 * without repeatedly requesting the same carrier data.
 *
 * Backed by React Query today, but can be migrated to another storage
 * implementation (e.g. IndexedDB) without changing consumers.
 */

export const carriersQueryOptions = (
  filters: Partial<CarrierFilters> = {},
  { enabled = true }: { enabled?: boolean } = {},
) =>
  queryOptions({
    enabled,
    queryFn: async ({ signal }) => {
      const carriers = await fetchCarriers(filters, { signal });
      put(carriers.list);
      return carriers;
    },
    queryKey: CACHE_KEYS.carriers(filters),
    // biome-ignore lint/style/noMagicNumbers: 5 minutes
    staleTime: 1000 * 60 * 5,
  });

const INTERNAL_CARRIERS_CACHE_KEY = btoa('internal-carriers-cache-key');

const INTERNAL_QUERY_CARRIERS_KEY = carriersQueryOptions({ carrierName: INTERNAL_CARRIERS_CACHE_KEY }).queryKey;

const getCarrierFallback = (carrierId: string): Carrier => {
  logger.warn(`Carrier "${carrierId}" was not found. Verify the data model.`);
  return {
    carrierName: carrierId,
    id: carrierId,
    label: carrierId,
  };
};

export const put = async (carriers: Carrier[]) => {
  // Sync with storage (currently React Query; may use IndexedDB in the future).
  const data = queryClient.getQueryData(INTERNAL_QUERY_CARRIERS_KEY);

  const newCarriers = carriers.reduce(
    (acc, carrier) => {
      acc[String(carrier.id)] = carrier;
      return acc;
    },
    { ...(data?.map ?? {}) },
  );

  queryClient.setQueryData(INTERNAL_QUERY_CARRIERS_KEY, {
    list: Object.values(newCarriers),
    map: newCarriers,
    total: Object.keys(newCarriers).length,
  });
};

export const getAll = (): Carriers | undefined => {
  return queryClient.getQueryData(INTERNAL_QUERY_CARRIERS_KEY);
};

export const find = async (carrierId: string): Promise<Carrier> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_CARRIERS_KEY);

  const carrier = data?.map[carrierId];
  if (carrier) {
    return carrier;
  }

  try {
    const newData = await queryClient.query(carriersQueryOptions({ search: { id: carrierId } }));
    if (newData.map[carrierId]) {
      void put(newData.list);
      return newData.map[carrierId];
    }
  } catch {
    return getCarrierFallback(carrierId);
  }

  return getCarrierFallback(carrierId);
};

export const findAll = async (carrierIds: string[]): Promise<Carrier[]> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_CARRIERS_KEY);

  const missingIds: string[] = [];
  const carriers: Carrier[] = [];

  for (const carrierId of carrierIds) {
    const c = data?.map[carrierId];
    if (c) {
      carriers.push(c);
    } else {
      missingIds.push(carrierId);
    }
  }

  if (!missingIds.length) {
    return carriers;
  }

  try {
    const newData = await queryClient.query(carriersQueryOptions({ search: { id: missingIds.join(',') } }));
    void put(newData.list);
    for (const missingId of missingIds) {
      const c = newData.map[missingId];
      if (c) {
        carriers.push(c);
      } else {
        carriers.push(getCarrierFallback(missingId));
      }
    }
  } catch {
    for (const missingId of missingIds) {
      carriers.push(getCarrierFallback(missingId));
    }
  }

  return carriers;
};
